import { BusinessKeywordsMapping } from 'api/client/mapping/business-keywords-mapping';
import { BusinessTypesMapping } from "api/client/mapping/business-types-mapping";
import { EssentialTypesMapping } from 'api/client/mapping/essential-types-mapping';
import { ChangeApproverApprovalStatusModel, TravelRequestAccommodationSegmentDetailDTO, TravelRequestAccommodationSegmentUpdateDTO, TravelRequestApprovalHistoryDetailDTO, TravelRequestBusinessContactUpdateDTO, TravelRequestCitySegmentDetailDTO, TravelRequestCitySegmentUpdateDTO, TravelRequestContractorDetailDTO, TravelRequestContractorUpdateDTO, TravelRequestCountryApprovalUpdateDTO, TravelRequestCountryApproverDetailDTO, TravelRequestCountryDetailDTO, TravelRequestCountryUpdateDTO, TravelRequestTravellerContractorCountryUpdateDTO, TravelRequestCreateDTO, TravelRequestDetailDTO, TravelRequestDetailModel, TravelRequestNoteSegmentDetailDTO, TravelRequestNoteSegmentUpdateDTO, TravelRequestPermissionsModel, TravelRequestQuestionDTO, TravelRequestsClient, TravelRequestSegmentDetailDTO, TravelRequestTransportationSegmentDetailDTO, TravelRequestTransportationSegmentUpdateDTO, TravelRequestTravellerDetailDTO, TravelRequestTravellerUpdateDTO, TravelRequestUpdateDTO } from 'api/swagger/ApiClient';
import { CountryApproverData, CountryRequestData, TravelRequestData, TravelRequestDetail, TravelRequestDetailApi, TravelRequestHistoryRecord, TravelRequestPermissions, TravelRequestResponse } from "api/travel-request-detail";
import { autoinject } from "aurelia-framework";
import { BusinessTraveler, ChecklistItem, ContractorTraveler, CorporateTraveler, CountryExperience, CountryRequest, TravelRequest } from "model/forms/travel-request";
import { AccommodationSegment, CitySegment, NoteSegment, TransportSegment, TravelSegment } from "model/forms/trip-details";
import { DeviationRequestDetailMapping } from './mapping/deviation-request-detail-mapping';
import { FileParameter, FileResponse, UploadedFile } from "model/essential-types";
import { EnumsService } from 'services/enums-service';

@autoinject
export class TravelRequestDetailClientApi implements TravelRequestDetailApi {
  constructor(private travelRequestsClient: TravelRequestsClient, private businessKeywordsMapping: BusinessKeywordsMapping, private essentialTypesMapping: EssentialTypesMapping, private businessTypesMapping: BusinessTypesMapping, private deviationRequestDetailMapping: DeviationRequestDetailMapping, private enumsService: EnumsService) {
  }

  async getTravelRequest(id: number): Promise<TravelRequestDetail> {
    return this.mapToTravelRequestDetail(await this.travelRequestsClient.getById(id));
  }

  async createTravelRequest(travelRequest: TravelRequest): Promise<TravelRequestDetail> {
    return this.mapToTravelRequestDetail(await this.travelRequestsClient.createTravelRequest(this.mapToTravelRequestCreateDTO(travelRequest)));
  }

  async updateTravelRequest(id: number, travelRequest: TravelRequest): Promise<TravelRequestDetail> {
    return this.mapToTravelRequestDetail(await this.travelRequestsClient.updateTravelRequest(id, this.mapToTravelRequestUpdateDTO(travelRequest)));
  }

  async archiveTravelRequest(id: number): Promise<TravelRequestDetail> {
    return this.mapToTravelRequestDetail(await this.travelRequestsClient.archiveTravelRequest(id));
  }

  async restoreTravelRequest(id: number): Promise<TravelRequestDetail> {
    return this.mapToTravelRequestDetail(await this.travelRequestsClient.unarchiveTravelRequest(id));
  }

  async submitApprovalResponse(id: number, response: TravelRequestResponse): Promise<TravelRequestDetail> {
    return this.mapToTravelRequestDetail(await this.travelRequestsClient.changeApproverApprovalStatus(id,
      new ChangeApproverApprovalStatusModel({
        approvalStatus: response.statusValue,
        approverId: response.approverId,
        countryId: response.countryId,
        comment: response.comment
      })
    ));
  }

  async uploadAttachment(id: number | null, file: FileParameter): Promise<UploadedFile> {
    return this.essentialTypesMapping.mapUploadedFileDetailToUploadedFile(id != null ? await this.travelRequestsClient.uploadAttachmentToTravelRequest(id, file) : await this.travelRequestsClient.uploadAttachment(file));
  }

  async downloadAttachment(id: number | null, attachment: UploadedFile): Promise<FileResponse> {
    return id != null ? this.travelRequestsClient.downloadAttachmentFromTravelRequest(id, attachment.id) : this.travelRequestsClient.downloadAttachment(attachment.id);
  }

  private mapToTravelRequestDetail(model: TravelRequestDetailModel): TravelRequestDetail {
    return {
      data: this.mapToTravelRequestData(model.travelRequest),
      airlinesApplicableDeviations: null, //this.mapToAirlinesApplicableDeviationRequests(model.airlinesApplicableDeviationRequests),
      history: model.travelRequest.approvalHistories.map(x => this.mapToTravelRequestHistoryRecord(x)),
      permissions: this.mapToTravelRequestPermissions(model.permissions)
    }
  }

  private mapToTravelRequestData(dto: TravelRequestDetailDTO): TravelRequestData {
    return {
      id: dto.id,
      name: dto.name,
      status: this.enumsService.approvalStatuses.find(x => x.value == dto.approvalStatus),
      businessDetails: {
        businessContacts: dto.businessContacts.map(x => {
          return {
            name: x.name,
            mail: x.email,
            phone: x.contactNumber
          }
        }),
        localArrangements: dto.localTravelArrangements,
        reasonForTravel: dto.reasonForTravel,
        travelType: this.businessKeywordsMapping.mapToTravelType(dto.travelType)
      },
      checklistItems: dto.questions.map(x => {
        return {
          checked: x.isChecked,
          message: x.name,
          mandatory: false
        }
      }),
      countryRequests: dto.countries.map(x => this.mapToCountryRequestData(x)),
      segments: dto.accommodationSegments.map(x => x as TravelRequestSegmentDetailDTO)
        .concat(dto.citySegments.map(x => x as TravelRequestSegmentDetailDTO))
        .concat(dto.transportationSegments.map(x => x as TravelRequestSegmentDetailDTO))
        .concat(dto.noteSegments.map(x => x as TravelRequestSegmentDetailDTO))
        .sort((a, b) => a.ordinalNumber - b.ordinalNumber)
        .map(x => {
          if (x instanceof TravelRequestAccommodationSegmentDetailDTO)
            return this.mapToAccommodationSegment(x as TravelRequestAccommodationSegmentDetailDTO);
          else if (x instanceof TravelRequestCitySegmentDetailDTO)
            return this.mapToCitySegment(x as TravelRequestCitySegmentDetailDTO);
          else if (x instanceof TravelRequestTransportationSegmentDetailDTO)
            return this.mapToTransportSegment(x as TravelRequestTransportationSegmentDetailDTO);
          else if (x instanceof TravelRequestNoteSegmentDetailDTO)
            return this.mapToNoteSegment(x as TravelRequestNoteSegmentDetailDTO);
          else
            throw new Error('Not supported segment type.');
        }),
      travelers: dto.travellers.map(x => this.mapToCorporateTraveler(x) as BusinessTraveler)
        .concat(dto.contractors.map(x => this.mapToContractorTraveler(x) as BusinessTraveler)),
      attachments: dto.attachments.map(x => this.essentialTypesMapping.mapUploadedFileDetailToUploadedFile(x)),
      createdBy: this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.createdBy),
      createdOn: dto.createdOn,
      modifiedBy: dto.modifiedBy !== null ? this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.modifiedBy) : null,
      modifiedOn: dto.modifiedOn,
      archivedBy: dto.archivedBy !== null ? this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.archivedBy) : null,
      archivedOn: dto.archivedOn
    }
  }

  private mapToCorporateTraveler(dto: TravelRequestTravellerDetailDTO): CorporateTraveler {
    return {
      kind: 'corporate-traveler',
      user: this.businessTypesMapping.mapUserToUserContact(dto.user),
      countryExperiences: dto.countries.map(x => {
        return {
          country: this.businessKeywordsMapping.mapToCountry(x.country),
          revisit: x.isRevisit
        }
      })
    }
  }

  private mapToContractorTraveler(dto: TravelRequestContractorDetailDTO): ContractorTraveler {
    return {
      kind: 'contractor-traveler',
      firstName: dto.firstName,
      lastName: dto.lastName,
      mail: dto.email,
      phone: dto.contactNumber,
      lineManager: this.businessTypesMapping.mapUserToUserContact(dto.lineManager),
      organisationEntity: this.businessKeywordsMapping.mapToOrganizationEntity(dto.organisationEntity),
      countryExperiences: dto.countries.map(x => {
        return {
          country: this.businessKeywordsMapping.mapToCountry(x.country),
          revisit: x.isRevisit
        }
      })
    }
  }

  private mapToCountryRequestData(dto: TravelRequestCountryDetailDTO): CountryRequestData {
    return {
      country: this.businessKeywordsMapping.mapToCountry(dto.country),
      transit: dto.isTransit,
      approvalLevel: this.businessKeywordsMapping.mapToApprovalLevel(dto.approvalLevel),
      conditionMessage: dto.specificCondition,
      approvalRequirementValue: dto.approvalRequirement,
      approval: dto.approval !== null ? {
        status: this.enumsService.approvalStatuses.find(x => x.value == dto.approval.approvalStatus),
        approvalTypeValue: dto.approval.approveType,
        approvers: dto.approval.approvers.map(x => this.mapToCountryApproverData(x))
      } : null
    }
  }

  private mapToCountryApproverData(dto: TravelRequestCountryApproverDetailDTO): CountryApproverData {
    return {
      approver: this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.approver),
      status: this.enumsService.approvalStatuses.find(x => x.value == dto.approvalStatus),
      comment: dto.comment,
      modifiedBy: dto.changedBy !== null ? this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.changedBy) : null,
      modifiedOn: dto.changedOn
    }
  }

  private mapToTravelRequestPermissions(dto: TravelRequestPermissionsModel): TravelRequestPermissions {
    return {
      canEdit: dto.canEdit,
      canArchive: dto.canArchive,
      canRestore: dto.canUnarchive,
      canApprove: dto.canChangeApproverApprovalStatus,
      approvableItems: dto.countryApprovers
    }
  }

  private mapToAccommodationSegment(dto: TravelRequestAccommodationSegmentDetailDTO): AccommodationSegment {
    return {
      kind: 'accommodation-segment',
      type: this.businessKeywordsMapping.mapToAccommodationType(dto.accommodationType),
      arrivalDate: dto.arrivalDate,
      departureDate: dto.departureDate,
      name: dto.name,
      address: dto.address,
      city: dto.city !== null ? this.businessKeywordsMapping.mapToCity(dto.city) : null,
      cityName: dto.city !== null ? dto.city.name : dto.cityCustomName,
      country: dto.country !== null ? this.businessKeywordsMapping.mapToCountry(dto.country) : null
    }
  }

  private mapToCitySegment(dto: TravelRequestCitySegmentDetailDTO): CitySegment {
    return {
      kind: 'city-segment',
      arrivalDate: dto.arrivalDate,
      departureDate: dto.departureDate,
      city: this.businessKeywordsMapping.mapToCity(dto.city)
    }
  }

  private mapToTransportSegment(dto: TravelRequestTransportationSegmentDetailDTO): TransportSegment {
    return {
      kind: 'transport-segment',
      type: this.businessKeywordsMapping.mapToTransportType(dto.transportationType),
      departureDate: dto.tripDate,
      airline: dto.airline !== null ? this.businessKeywordsMapping.mapToAirline(dto.airline) : null,
      airlineName: dto.airline !== null ? dto.airline.name : dto.airlineCustomName,
      flightNumber: dto.flightNumber,
      note: dto.note
    }
  }

  private mapToNoteSegment(dto: TravelRequestNoteSegmentDetailDTO): NoteSegment {
    return {
      kind: 'note-segment',
      note: dto.note
    }
  }

  // private mapToAirlinesApplicableDeviationRequests(dtos: IAirlineApplicableDeviationRequestDTO[]): AirlineApplicableDeviationRequests[] {
  //   const self = this;
  //   return dtos.reduce<AirlineApplicableDeviationRequests[]>(function (acc, obj) {
  //     const applicableDeviationRequest = {
  //       id: obj.deviationRequestId,
  //       deviationType: obj.deviationType,
  //       detail: self.deviationRequestDetailMapping.mapToDeviationDetail(obj.detail),
  //       validFrom: obj.validFrom,
  //       validTo: obj.validTo,
  //       additionalInfo: obj.additionalInfo
  //     } as ApplicableDeviationRequest;
  //     if (obj.airlineId !== null) {
  //       if (!acc.some(x => x.airlineId === obj.airlineId)) {
  //         acc.push({
  //           airlineId: obj.airlineId,
  //           applicableDeviationRequest: []
  //         } as AirlineApplicableDeviationRequests);
  //       }
  //       const airlineApplicable = acc.filter(x => x.airlineId === obj.airlineId)[0];
  //       airlineApplicable.applicableDeviationRequest.push(applicableDeviationRequest);
  //     }
  //     else if (obj.airlineCustomName !== null) {
  //       if (!acc.some(x => x.airlineCustomName === obj.airlineCustomName)) {
  //         acc.push({
  //           airlineCustomName: obj.airlineCustomName,
  //           applicableDeviationRequest: []
  //         } as AirlineApplicableDeviationRequests);
  //       }
  //       const airlineApplicable = acc.filter(x => x.airlineCustomName === obj.airlineCustomName)[0];
  //       airlineApplicable.applicableDeviationRequest.push(applicableDeviationRequest);
  //     }
  //     return acc;
  //   }, [])
  // }

  private mapToTravelRequestHistoryRecord(dto: TravelRequestApprovalHistoryDetailDTO): TravelRequestHistoryRecord {
    return {
      requestStatus: this.enumsService.approvalStatuses.find(x => x.value == dto.overallApprovalStatus),
      approvalStatus: this.enumsService.approvalStatuses.find(x => x.value == dto.approvalStatus),
      country: dto.country !== null ? this.businessKeywordsMapping.mapToCountry(dto.country) : null,
      approver: dto.approver !== null ? this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.approver) : null,
      comment: dto.comment,
      createdBy: this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.createdBy),
      createdOn: dto.createdOn
    }
  }

  private mapToTravelRequestCreateDTO(travelRequest: TravelRequest): TravelRequestCreateDTO {
    const segmentDTOs = this.getSegmentsDTOs(travelRequest.segments);
    return new TravelRequestCreateDTO({
      name: travelRequest.name,
      travellers: travelRequest.travelers.filter(x => x.kind === 'corporate-traveler').map(x => this.mapToTravelRequestTravellerUpdateDTO(x as CorporateTraveler)),
      contractors: travelRequest.travelers.filter(x => x.kind === 'contractor-traveler').map(x => this.mapToTravelRequestContractorUpdateDTO(x as ContractorTraveler)),
      countries: travelRequest.countryRequests.map(x => this.mapToTravelRequestCountryApprovalUpdateDTO(x)),
      accommodationSegments: segmentDTOs.filter(x => x instanceof TravelRequestAccommodationSegmentUpdateDTO).map(x => x as TravelRequestAccommodationSegmentUpdateDTO),
      citySegments: segmentDTOs.filter(x => x instanceof TravelRequestCitySegmentUpdateDTO).map(x => x as TravelRequestCitySegmentUpdateDTO),
      transportationSegments: segmentDTOs.filter(x => x instanceof TravelRequestTransportationSegmentUpdateDTO).map(x => x as TravelRequestTransportationSegmentUpdateDTO),
      noteSegments: segmentDTOs.filter(x => x instanceof TravelRequestNoteSegmentUpdateDTO).map(x => x as TravelRequestNoteSegmentUpdateDTO),
      reasonForTravel: travelRequest.businessDetails.reasonForTravel,
      localTravelArrangements: travelRequest.businessDetails.localArrangements,
      travelTypeId: travelRequest.businessDetails.travelType.id,
      businessContacts: travelRequest.businessDetails.businessContacts.map(x => {
        return new TravelRequestBusinessContactUpdateDTO({
          name: x.name,
          contactNumber: x.phone,
          email: x.mail
        })
      }),
      questions: travelRequest.checklistItems.map(x => this.mapToTravelRequestQuestionUpdateDTO(x)),
      uploadedFilesIds: travelRequest.attachments.map(x => x.id)
    });
  }

  private mapToTravelRequestUpdateDTO(travelRequest: TravelRequest): TravelRequestUpdateDTO {
    const segmentDTOs = this.getSegmentsDTOs(travelRequest.segments);
    return new TravelRequestUpdateDTO({
      name: travelRequest.name,
      travellers: travelRequest.travelers.filter(x => x.kind === 'corporate-traveler').map(x => this.mapToTravelRequestTravellerUpdateDTO(x as CorporateTraveler)),
      contractors: travelRequest.travelers.filter(x => x.kind === 'contractor-traveler').map(x => this.mapToTravelRequestContractorUpdateDTO(x as ContractorTraveler)),
      countries: travelRequest.countryRequests.map(x => this.mapToTravelRequestCountryApprovalUpdateDTO(x)),
      accommodationSegments: segmentDTOs.filter(x => x instanceof TravelRequestAccommodationSegmentUpdateDTO).map(x => x as TravelRequestAccommodationSegmentUpdateDTO),
      citySegments: segmentDTOs.filter(x => x instanceof TravelRequestCitySegmentUpdateDTO).map(x => x as TravelRequestCitySegmentUpdateDTO),
      transportationSegments: segmentDTOs.filter(x => x instanceof TravelRequestTransportationSegmentUpdateDTO).map(x => x as TravelRequestTransportationSegmentUpdateDTO),
      noteSegments: segmentDTOs.filter(x => x instanceof TravelRequestNoteSegmentUpdateDTO).map(x => x as TravelRequestNoteSegmentUpdateDTO),
      reasonForTravel: travelRequest.businessDetails.reasonForTravel,
      localTravelArrangements: travelRequest.businessDetails.localArrangements,
      travelTypeId: travelRequest.businessDetails.travelType.id,
      businessContacts: travelRequest.businessDetails.businessContacts.map(x => {
        return new TravelRequestBusinessContactUpdateDTO({
          name: x.name,
          contactNumber: x.phone,
          email: x.mail
        })
      }),
      questions: travelRequest.checklistItems.map(x => this.mapToTravelRequestQuestionUpdateDTO(x)),
      uploadedFilesIds: travelRequest.attachments.map(x => x.id)
    });
  }

  private getSegmentsDTOs(segments: TravelSegment[]): (TravelRequestAccommodationSegmentUpdateDTO | TravelRequestCitySegmentUpdateDTO | TravelRequestTransportationSegmentUpdateDTO | TravelRequestNoteSegmentUpdateDTO)[] {
    let ordinalNumber = 0;
    return segments.map(x => {
      if (x.kind === 'accommodation-segment')
        return this.mapToTravelRequestAccommodationSegmentUpdateDTO(x as AccommodationSegment, ordinalNumber++);
      else if (x.kind === 'city-segment')
        return this.mapToTravelRequestCitySegmentUpdateDTO(x as CitySegment, ordinalNumber++);
      else if (x.kind === 'transport-segment')
        return this.mapToTravelRequestTransportationSegmentUpdateDTO(x as TransportSegment, ordinalNumber++);
      else if (x.kind === 'note-segment')
        return this.mapToTravelRequestNoteSegmentUpdateDTO(x as NoteSegment, ordinalNumber++);
      else
        throw new Error('Not supported segment type.');
    })
  }

  private mapToTravelRequestCountryApprovalUpdateDTO(countryRequest: CountryRequest): TravelRequestCountryUpdateDTO {
    return new TravelRequestCountryUpdateDTO({
      countryId: countryRequest.country.id,
      approvalLevelId: countryRequest.approvalLevel.id,
      approvalRequirement: countryRequest.approvalRequirementValue.valueOf(),
      isTransit: countryRequest.transit,
      approval: countryRequest.approval !== null ? new TravelRequestCountryApprovalUpdateDTO({
        approveType: countryRequest.approval.approvalTypeValue.valueOf(),
        approversIds: countryRequest.approval.approvers.map(a => a.approver.id)
      }) : null
    })
  }

  private mapToTravelRequestAccommodationSegmentUpdateDTO(accommodationSegment: AccommodationSegment, ordinalNumber: number): TravelRequestAccommodationSegmentUpdateDTO {
    return new TravelRequestAccommodationSegmentUpdateDTO({
      accommodationTypeId: accommodationSegment.type.id,
      arrivalDate: accommodationSegment.arrivalDate,
      departureDate: accommodationSegment.departureDate,
      name: accommodationSegment.name,
      address: accommodationSegment.address,
      cityId: accommodationSegment.city != null ? accommodationSegment.city.id : null,
      cityCustomName: accommodationSegment.city != null ? accommodationSegment.city.name : accommodationSegment.cityName,
      countryId: accommodationSegment.country != null ? accommodationSegment.country.id : null,
      ordinalNumber: ordinalNumber
    })
  }

  private mapToTravelRequestCitySegmentUpdateDTO(citySegment: CitySegment, ordinalNumber: number): TravelRequestCitySegmentUpdateDTO {
    return new TravelRequestCitySegmentUpdateDTO({
      arrivalDate: citySegment.arrivalDate,
      departureDate: citySegment.departureDate,
      cityId: citySegment.city.id,
      ordinalNumber: ordinalNumber
    })
  }

  private mapToTravelRequestTransportationSegmentUpdateDTO(transportSegment: TransportSegment, ordinalNumber: number): TravelRequestTransportationSegmentUpdateDTO {
    return new TravelRequestTransportationSegmentUpdateDTO({
      transportationTypeId: transportSegment.type.id,
      tripDate: transportSegment.departureDate,
      airlineId: transportSegment.airline != null ? transportSegment.airline.id : null,
      airlineCustomName: transportSegment.airline != null ? transportSegment.airline.name : transportSegment.airlineName,
      flightNumber: transportSegment.flightNumber,
      note: transportSegment.note,
      ordinalNumber: ordinalNumber
    })
  }

  private mapToTravelRequestNoteSegmentUpdateDTO(noteSegment: NoteSegment, ordinalNumber: number): TravelRequestNoteSegmentUpdateDTO {
    return new TravelRequestNoteSegmentUpdateDTO({
      note: noteSegment.note,
      ordinalNumber: ordinalNumber
    })
  }

  private mapToTravelRequestTravellerUpdateDTO(corporateTraveler: CorporateTraveler): TravelRequestTravellerUpdateDTO {
    return new TravelRequestTravellerUpdateDTO({
      userUniqueId: corporateTraveler.user.uniqueid,
      countries: corporateTraveler.countryExperiences.map(x => this.mapToTravelRequestTravellerCountryUpdateDTO(x))
    })
  }

  private mapToTravelRequestContractorUpdateDTO(contractorTraveler: ContractorTraveler): TravelRequestContractorUpdateDTO {
    return new TravelRequestContractorUpdateDTO({
      firstName: contractorTraveler.firstName,
      lastName: contractorTraveler.lastName,
      email: contractorTraveler.mail,
      contactNumber: contractorTraveler.phone,
      lineManagerUniqueId: contractorTraveler.lineManager.uniqueid,
      organisationEntityId: contractorTraveler.organisationEntity.id,
      countries: contractorTraveler.countryExperiences.map(x => this.mapToTravelRequestTravellerCountryUpdateDTO(x))
    })
  }

  private mapToTravelRequestTravellerCountryUpdateDTO(countryExperience: CountryExperience): TravelRequestTravellerContractorCountryUpdateDTO {
    return new TravelRequestTravellerContractorCountryUpdateDTO({
      countryId: countryExperience.country.id,
      isRevisit: countryExperience.revisit
    })
  }

  private mapToTravelRequestQuestionUpdateDTO(checkListItem: ChecklistItem): TravelRequestQuestionDTO {
    return new TravelRequestQuestionDTO({
      name: checkListItem.message,
      isChecked: checkListItem.checked
    })
  }
}
